import type { JSX } from 'react';
import { useRef, useState, type KeyboardEvent } from 'react';
import type { ListeningMode, VoiceSnapshot } from '../voice/controller';
import { describeShortcut } from '../voice/shortcuts';
import { useT } from '../i18n/useT';

export const COMPOSER_ATTR = 'data-composer';

export interface ComposerProps {
  onSend: (text: string) => void;
  voiceState: VoiceSnapshot;
  mode: ListeningMode;
  toggleShortcut: string;
  pushToTalkShortcut: string;
  onMicToggle: () => void;
  onMicPressStart: () => void;
  onMicPressEnd: () => void;
  busy: boolean;
}

export function Composer(props: ComposerProps): JSX.Element {
  const t = useT();
  const [text, setText] = useState('');
  const ref = useRef<HTMLTextAreaElement>(null);
  const active = props.voiceState.status !== 'idle' && props.voiceState.status !== 'error';
  const hold = props.mode === 'PRESS_AND_HOLD';

  const send = (): void => {
    const value = text.trim();
    if (!value) return;
    props.onSend(value);
    setText('');
    ref.current?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      send();
    }
  };

  const hint = hold
    ? t('shortcutHoldHint', { shortcut: describeShortcut(props.toggleShortcut) })
    : `${t('shortcutHint', { shortcut: describeShortcut(props.toggleShortcut) })} · ${t('shortcutHoldHint', { shortcut: describeShortcut(props.pushToTalkShortcut) })}`;

  return (
    <footer className="composer">
      <div className="composer-row">
        <textarea
          ref={ref}
          {...{ [COMPOSER_ATTR]: 'true' }}
          className="composer-input"
          rows={1}
          value={text}
          placeholder={t('composerPlaceholder')}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          aria-label={t('composerPlaceholder')}
          data-testid="composer-input"
        />
        <button type="button" className="btn btn-primary" onClick={send} disabled={!text.trim() || props.busy} aria-label={t('send')} data-testid="send-button">
          <span className="send-text">{t('send')}</span>
          <span className="send-icon" aria-hidden="true">➤</span>
        </button>
        <button
          type="button"
          className={active ? 'btn mic active' : 'btn mic'}
          aria-pressed={active}
          aria-label={hold ? t('micHold') : active ? t('micStop') : t('micStart')}
          title={hold ? t('micHold') : active ? t('micStop') : t('micStart')}
          data-testid="mic-button"
          onClick={hold ? undefined : props.onMicToggle}
          onPointerDown={hold ? (e) => { e.preventDefault(); props.onMicPressStart(); } : undefined}
          onPointerUp={hold ? props.onMicPressEnd : undefined}
          onPointerLeave={hold ? props.onMicPressEnd : undefined}
          onPointerCancel={hold ? props.onMicPressEnd : undefined}
          onKeyDown={hold ? (e) => { if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) { e.preventDefault(); props.onMicPressStart(); } } : undefined}
          onKeyUp={hold ? (e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); props.onMicPressEnd(); } } : undefined}
        >
          <span aria-hidden="true">{active ? '■' : '🎤'}</span>
        </button>
      </div>
      <div className="composer-hint muted small">
        <span>{hint}</span>
        <span>{t('privacyNote')}</span>
      </div>
    </footer>
  );
}
